import { useState, useRef } from 'react'
import { FolderPlus, Pencil, Trash2, Check, X } from 'lucide-react'
import { type AdminAsset, type AssetFolder } from '../lib/api'

interface FolderSidebarProps {
  folders: AssetFolder[]
  assets: AdminAsset[]
  activeFolderId: string | null
  onFolderSelect: (folderId: string | null) => void
  onFolderCreate: (name: string) => Promise<void>
  onFolderRename: (id: string, name: string) => Promise<void>
  onFolderDelete: (id: string, fileCount: number) => void
  onAssetDrop: (assetId: string, targetFolderId: string | null) => Promise<void>
}

function FolderItem({
  folder,
  count,
  isActive,
  onSelect,
  onRename,
  onDelete,
  onAssetDrop,
}: {
  folder: AssetFolder
  count: number
  isActive: boolean
  onSelect: () => void
  onRename: (name: string) => Promise<void>
  onDelete: () => void
  onAssetDrop: (assetId: string) => Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(folder.name)
  const [saving, setSaving] = useState(false)
  const [dragOver, setDragOver] = useState(false)

  const handleSave = async () => {
    if (!draft.trim() || draft.trim() === folder.name) { setEditing(false); return }
    setSaving(true)
    try { await onRename(draft.trim()); setEditing(false) }
    finally { setSaving(false) }
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const assetId = e.dataTransfer.getData('assetId')
    if (assetId) await onAssetDrop(assetId)
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
        isActive ? 'bg-yellow-500/10 text-yellow-400' : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200'
      } ${dragOver ? 'bg-yellow-500/20 ring-1 ring-yellow-500' : ''}`}
    >
      {editing ? (
        <div className="flex items-center gap-1 flex-1" onClick={(e) => e.stopPropagation()}>
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave()
              if (e.key === 'Escape') { setEditing(false); setDraft(folder.name) }
            }}
            className="flex-1 bg-zinc-700 text-white text-xs rounded px-2 py-0.5 border border-zinc-500 focus:outline-none focus:border-yellow-400 min-w-0"
          />
          <button type="button" onClick={handleSave} disabled={saving} className="text-green-400 hover:text-green-300">
            <Check size={12} />
          </button>
          <button type="button" onClick={() => { setEditing(false); setDraft(folder.name) }} className="text-zinc-500 hover:text-white">
            <X size={12} />
          </button>
        </div>
      ) : (
        <>
          <span className="text-sm flex-1 truncate" onClick={onSelect}>📁 {folder.name}</span>
          <span className="text-[10px] text-zinc-500 tabular-nums">{count}</span>
          <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5">
            <button type="button"
              onClick={(e) => { e.stopPropagation(); setDraft(folder.name); setEditing(true) }}
              className="p-0.5 text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              <Pencil size={11} />
            </button>
            <button type="button"
              onClick={(e) => { e.stopPropagation(); if (count > 0) return; onDelete() }}
              disabled={count > 0}
              title={count > 0 ? `Folder berisi ${count} file — pindahkan atau hapus semua file terlebih dahulu` : 'Hapus folder'}
              className={`p-0.5 transition-colors ${count > 0 ? 'text-zinc-700 cursor-not-allowed' : 'text-zinc-500 hover:text-red-400'}`}
            >
              <Trash2 size={11} />
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default function FolderSidebar({
  folders,
  assets,
  activeFolderId,
  onFolderSelect,
  onFolderCreate,
  onFolderRename,
  onFolderDelete,
  onAssetDrop,
}: FolderSidebarProps) {
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [creating, setCreating] = useState(false)
  const [allDragOver, setAllDragOver] = useState(false)
  const [uncategorizedDragOver, setUncategorizedDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const countFor = (folderId: string) => assets.filter((a) => a.folder_id === folderId).length

  const handleCreate = async () => {
    if (!newFolderName.trim()) return
    setCreating(true)
    try {
      await onFolderCreate(newFolderName.trim())
      setNewFolderName('')
      setCreatingFolder(false)
    } finally {
      setCreating(false)
    }
  }

  const handleAllFilesDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setAllDragOver(false)
    const assetId = e.dataTransfer.getData('assetId')
    if (assetId) await onAssetDrop(assetId, null)
  }

  return (
    <div className="w-52 flex-shrink-0 border-r border-zinc-800 flex flex-col h-full">
      <div className="p-3 flex-1 overflow-y-auto space-y-0.5">
        {/* All Files */}
        <div
          onDragOver={(e) => { e.preventDefault(); setAllDragOver(true) }}
          onDragLeave={() => setAllDragOver(false)}
          onDrop={handleAllFilesDrop}
          onClick={() => onFolderSelect(null)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
            activeFolderId === null ? 'bg-yellow-500/10 text-yellow-400' : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200'
          } ${allDragOver ? 'bg-yellow-500/20 ring-1 ring-yellow-500' : ''}`}
        >
          <span className="text-sm flex-1">📁 All Files</span>
          <span className="text-[10px] text-zinc-500 tabular-nums">{assets.length}</span>
        </div>

        {/* Tanpa Folder (uncategorized) */}
        <div
          onDragOver={(e) => { e.preventDefault(); setUncategorizedDragOver(true) }}
          onDragLeave={() => setUncategorizedDragOver(false)}
          onDrop={async (e) => {
            e.preventDefault()
            setUncategorizedDragOver(false)
            const assetId = e.dataTransfer.getData('assetId')
            if (assetId) await onAssetDrop(assetId, null)
          }}
          onClick={() => onFolderSelect('__uncategorized__')}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
            activeFolderId === '__uncategorized__' ? 'bg-yellow-500/10 text-yellow-400' : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200'
          } ${uncategorizedDragOver ? 'bg-yellow-500/20 ring-1 ring-yellow-500' : ''}`}
        >
          <span className="text-sm flex-1">📂 Tanpa Folder</span>
          <span className="text-[10px] text-zinc-500 tabular-nums">{assets.filter((a) => a.folder_id === null).length}</span>
        </div>

        {folders.length > 0 && <div className="border-t border-zinc-800 my-1" />}

        {folders.map((folder) => (
          <FolderItem
            key={folder.id}
            folder={folder}
            count={countFor(folder.id)}
            isActive={activeFolderId === folder.id}
            onSelect={() => onFolderSelect(folder.id)}
            onRename={(name) => onFolderRename(folder.id, name)}
            onDelete={() => onFolderDelete(folder.id, countFor(folder.id))}
            onAssetDrop={(assetId) => onAssetDrop(assetId, folder.id)}
          />
        ))}

        {folders.length === 0 && (
          <p className="text-zinc-600 text-[10px] px-3 pt-2 italic">No folders yet.</p>
        )}
      </div>

      {/* New Folder */}
      <div className="p-3 border-t border-zinc-800">
        {creatingFolder ? (
          <div className="flex items-center gap-1">
            <input
              ref={inputRef}
              autoFocus
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate()
                if (e.key === 'Escape') { setCreatingFolder(false); setNewFolderName('') }
              }}
              placeholder="Folder name"
              className="flex-1 bg-zinc-700 text-white text-xs rounded px-2 py-1 border border-zinc-500 focus:outline-none focus:border-yellow-400 min-w-0"
            />
            <button type="button" onClick={handleCreate} disabled={creating} className="text-green-400 hover:text-green-300">
              <Check size={13} />
            </button>
            <button type="button" onClick={() => { setCreatingFolder(false); setNewFolderName('') }} className="text-zinc-500 hover:text-white">
              <X size={13} />
            </button>
          </div>
        ) : (
          <button type="button"
            onClick={() => setCreatingFolder(true)}
            className="flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-300 transition-colors w-full"
          >
            <FolderPlus size={13} />
            New Folder
          </button>
        )}
      </div>
    </div>
  )
}
